import { FC } from "react";
import { Typography } from "@mui/material";
import { isArray } from "lodash";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import Fieldset from "src/Pages/Company/Absences/Forms/Fieldset";
import {
  CustomFieldValues,
  ICustomFieldsConfiguration,
} from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";

interface IAbsenceRequestCustomFieldsPreviewProps {
  customFieldsConfiguration: ICustomFieldsConfiguration;
  customFieldValues: CustomFieldValues;
}

/**
 * Processes custom fields configuration and values to create a formatted array of fields for display.
 *
 * @param customFieldsConfiguration - The configuration object from AbsenceType containing custom fields definitions.
 * @param customFieldValues - An object containing the values for custom fields, keyed by field ID.
 * @returns An array of processed custom fields with formatted values, filtered to exclude fields with null values.
 */
const processCustomFields = (
  customFieldsConfiguration: ICustomFieldsConfiguration,
  customFieldValues: CustomFieldValues
) =>
  customFieldsConfiguration.customFields
    .map((field) => {
      const value = customFieldValues[field.customFieldId!];

      return {
        id: field.customFieldId,
        name: field.name,
        description: field.description,
        label: field.label,
        value: isArray(value) ? value.join(", ") : value,
      };
    })
    .filter((field) => field.value !== null);

const AbsenceRequestCustomFieldsPreview: FC<
  IAbsenceRequestCustomFieldsPreviewProps
> = ({ customFieldsConfiguration, customFieldValues }) => {
  const logStatus = useLogStatus();

  if (!customFieldsConfiguration) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No customFieldsConfiguration provided.",
      file: "AbsenceRequestCustomFieldsPreview.tsx",
    });
  }

  if (!customFieldValues) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No customFieldValues provided.",
      file: "AbsenceRequestCustomFieldsPreview.tsx",
    });
  }

  const processedFields = processCustomFields(
    customFieldsConfiguration,
    customFieldValues
  );

  return processedFields.map(({ id, name, description, label, value }) => (
    <Fieldset
      key={id}
      legend={name}
      description={description}
      data-testid={`absence-request-preview__field-${id}`}
    >
      <Typography variant="body1">{label || value}</Typography>
    </Fieldset>
  ));
};

export default AbsenceRequestCustomFieldsPreview;
